import "./Gallery.css";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchGallery } from "@genfun/core/api/API";
import { GalleryPagination } from "../components/Pagination";
import SupportEmail from "../components/SupportEmail";
import LegalNotice from "../components/LegalNotice";

// URL parameter helpers
const parseType = (paramValue) => {
    return (paramValue === 'video' || paramValue === 'photo') ? paramValue : 'photo';
};

const parseSort = (paramValue) => {
    return (paramValue === 'latest' || paramValue === 'popular') ? paramValue : 'popular';
};

const MAX_ITEMS = 20;

// Gallery item component
function GalleryItem({ item, selectedType }) {
    const [videoLoaded, setVideoLoaded] = useState(false);

    const handleReport = (e) => {
        e.stopPropagation();
        window.location.href = `/take_it_down_report?media_id=${encodeURIComponent(item.media_id)}`;
    };

    return (
        <div className="galleryItem">
            <button
                className="galleryItemReportCorner"
                onClick={handleReport}
                title="Report content"
            >
                Report
            </button>
            <div className="galleryItemContent">
                {selectedType === 'video' ? (
                    <>
                        {!videoLoaded && item.frame && (
                            <img
                                src={item.frame}
                                alt={`Generation ${item.id}`}
                                className="galleryImage"
                            />
                        )}
                        <video
                            src={item.url}
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            className="galleryImage"
                            playsInline={true}
                            style={{ display: videoLoaded ? 'block' : 'none' }}
                            onLoadedData={() => setVideoLoaded(true)}
                        />
                    </>
                ) : (
                    <img
                        src={item.url}
                        alt={`Generation ${item.id}`}
                        className="galleryImage"
                        loading="lazy"
                    />
                )}
            </div>
        </div>
    );
}

// Empty state for personal gallery
function GalleryEmptyState() {
    return (
        <div className="galleryEmptyState">
            <p className="galleryEmptyText">
                You haven't generated or saved anything yet
            </p>
            <button
                className="galleryEmptyButton"
                onClick={() => window.location.href = "/main"}
            >
                Generate
            </button>
        </div>
    );
}

export function Gallery({ token, user, setUser, setSignedIn }) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize from URL params with defaults
    const pageParam = parseInt(searchParams.get('page')) || 1;
    const typeParam = searchParams.get('type');
    const sortParam = searchParams.get('sort');

    // Redirect to sign-in when not logged in (personal gallery only)
    useEffect(() => {
        const storedToken = localStorage.getItem("igenfuntoken");
        if (!user && !storedToken) {
            window.location.href = `/login`;
        }
    }, [user]);

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(pageParam);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [selectedType, setSelectedType] = useState(() => parseType(typeParam));
    const [sortBy, setSortBy] = useState(() => parseSort(sortParam));

    // Track previous filter values to detect actual changes vs initial mount
    const prevFiltersRef = useRef({ type: null, sort: null });

    // Sync filters to URL and reset page when filters change
    useEffect(() => {
        const prevType = prevFiltersRef.current.type;
        const prevSort = prevFiltersRef.current.sort;

        // Store current values for next comparison
        prevFiltersRef.current = { type: selectedType, sort: sortBy };

        // Skip on initial mount (prev values are null)
        if (prevType === null && prevSort === null) {
            return;
        }

        // Check if filters actually changed
        const typeChanged = prevType !== selectedType;
        const sortChanged = prevSort !== sortBy;

        if (!typeChanged && !sortChanged) {
            return;
        }

        // Filters changed - update URL with new filters AND reset page to 1
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);

            if (selectedType !== 'photo') {
                newParams.set('type', selectedType);
            } else {
                newParams.delete('type');
            }

            if (sortBy !== 'popular') {
                newParams.set('sort', sortBy);
            } else {
                newParams.delete('sort');
            }

            newParams.set('page', '1');

            return newParams;
        });
    }, [selectedType, sortBy, setSearchParams]);

    // Sync currentPage with URL parameter
    useEffect(() => {
        setCurrentPage(pageParam);
    }, [pageParam]);

    const loadMoreItems = useCallback(async () => {
        if (loading || !hasMore) return;

        // Use token prop, fallback to localStorage for initial render timing
        const effectiveToken = token || (() => {
            try {
                return JSON.parse(localStorage.getItem("igenfuntoken"));
            } catch {
                return null;
            }
        })();

        // Wait for token before fetching personal gallery
        if (!effectiveToken) return;

        setLoading(true);
        try {
            const isVideo = selectedType === 'video';
            const offset = (currentPage - 1) * MAX_ITEMS;
            const sortPopular = sortBy === 'popular';
            const sortNewest = sortBy === 'latest';
            const newItems = await fetchGallery([], offset, isVideo, effectiveToken, false, sortPopular, sortNewest);

            if (newItems.content.length === 0) {
                setHasMore(false);
            } else {
                setItems(newItems.content);
                setTotalPages(Math.ceil(newItems.total / MAX_ITEMS));
            }
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, selectedType, currentPage, token, sortBy]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;

        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('page', newPage.toString());
            return newParams;
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Reset items when page changes
    useEffect(() => {
        setItems([]);
        setHasMore(true);
    }, [currentPage]);

    // Load initial items
    useEffect(() => {
        if (items.length === 0 && !loading) {
            loadMoreItems();
        }
    }, [items.length, loading, loadMoreItems]);

    const showEmptyState = !loading && items.length === 0 && !hasMore;

    // Handler functions for immediate visual feedback
    const handleTypeChange = (newType) => {
        if (newType === selectedType) return;
        setSelectedType(newType);
        setCurrentPage(1);
        setItems([]);
        setHasMore(true);
    };

    const handleSortChange = (newSort) => {
        if (newSort === sortBy) return;
        setSortBy(newSort);
        setCurrentPage(1);
        setItems([]);
        setHasMore(true);
    };

    return (
        <div className="galleryPage">
            <div className="galleryContent">
                {/* All tabs in one row */}
                <div className="galleryTabsRow">
                    {/* Secondary tabs */}
                    <div className="galleryTabsRight">
                        <div className="gallerySecondaryTabs">
                            <button
                                className={`gallerySecondaryTab ${selectedType === 'photo' ? 'gallerySecondaryTabActive' : ''}`}
                                onClick={() => handleTypeChange('photo')}
                            >
                                Photo
                            </button>
                            <button
                                className={`gallerySecondaryTab ${selectedType === 'video' ? 'gallerySecondaryTabActive' : ''}`}
                                onClick={() => handleTypeChange('video')}
                            >
                                Video
                            </button>
                        </div>
                        <div className="gallerySecondaryTabs">
                            <button
                                className={`gallerySecondaryTab ${sortBy === 'popular' ? 'gallerySecondaryTabActive' : ''}`}
                                onClick={() => handleSortChange('popular')}
                            >
                                Hot
                            </button>
                            <button
                                className={`gallerySecondaryTab ${sortBy === 'latest' ? 'gallerySecondaryTabActive' : ''}`}
                                onClick={() => handleSortChange('latest')}
                            >
                                New
                            </button>
                        </div>
                    </div>
                </div>

                {/* Gallery content */}
                {showEmptyState ? (
                    <GalleryEmptyState />
                ) : (
                    <>
                        <div className="galleryGrid">
                            {items.map((item) => (
                                <GalleryItem
                                    key={item.media_id}
                                    item={item}
                                    selectedType={selectedType}
                                />
                            ))}
                        </div>

                        {loading && (
                            <div className="galleryLoader">
                                <div className="loader"></div>
                            </div>
                        )}

                        {hasMore && !loading && items.length > 0 && (
                            <GalleryPagination
                                currentPage={currentPage}
                                total={totalPages}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="galleryFooter">
                <p className="galleryFooterText">
                    All depicted characters are entirely fictional and are not based on any real individuals.
                    Any resemblance to actual persons, living or dead, is purely coincidental.
                </p>
                <SupportEmail />
                <div className="galleryFooterLinks">
                    <a href="/take_it_down_report" className="galleryFooterLink">Report content</a>
                </div>
                <LegalNotice />
            </div>
        </div>
    );
}
